import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Section = {
  emoji: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

const LAST_UPDATED = "Ιούνιος 2026";

const sections: Section[] = [
  {
    emoji: "🐼",
    title: "Ποιοί είμαστε (Υπεύθυνος Επεξεργασίας)",
    paragraphs: [
      "Υπεύθυνος επεξεργασίας των προσωπικών σου δεδομένων είναι η ατομική επιχείρηση «ΑΣΛΑΝΟΓΛΟΥ ΑΙΚΑΤΕΡΙΝΗ ΒΑΣΙΛΕΙΟΣ» (διακριτικός τίτλος MrPanda), που εδρεύει στην οδό Π. Λεβαντή 15Α, 564 29 Σταυρούπολη Θεσσαλονίκης, με ΑΦΜ 117875310, Αριθμό Γ.Ε.ΜΗ. 156171904000, ΔΟΥ ΚΕΦΟΔΕ ΘΕΣΣΑΛΟΝΙΚΗΣ, και λειτουργεί το ηλεκτρονικό κατάστημα www.mrpanda.gr.",
      "Σεβόμαστε τα προσωπικά σου δεδομένα και τα επεξεργαζόμαστε σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΕΕ) 2016/679 (GDPR), τον Ν. 4624/2019 και τον Ν. 3471/2006. Για οποιοδήποτε θέμα σχετικά με τα δεδομένα σου, μπορείς να επικοινωνείς στο info@mrpanda.gr.",
    ],
  },
  {
    emoji: "📋",
    title: "Ποια δεδομένα συλλέγουμε",
    paragraphs: ["Ανάλογα με το πώς χρησιμοποιείς το κατάστημά μας, ενδέχεται να συλλέγουμε:"],
    bullets: [
      "Στοιχεία λογαριασμού & επικοινωνίας: όνομα, επώνυμο, email, τηλέφωνο.",
      "Στοιχεία παραγγελίας & αποστολής: διεύθυνση, ΤΚ, πόλη, ιστορικό παραγγελιών, προτιμήσεις παράδοσης.",
      "Στοιχεία τιμολόγησης (όταν ζητάς τιμολόγιο): επωνυμία, ΑΦΜ, επάγγελμα/δραστηριότητα, ΔΟΥ.",
      "Στοιχεία πληρωμής: η πληρωμή με κάρτα διεκπεραιώνεται από τον πάροχο (Viva.com) — δεν αποθηκεύουμε εμείς τα στοιχεία της κάρτας σου.",
      "Στοιχεία επικοινωνίας: μηνύματα που μας στέλνεις μέσω της φόρμας επικοινωνίας.",
      "Περιεχόμενο αξιολογήσεων: σχόλια, βαθμολογίες και τυχόν φωτογραφίες που υποβάλλεις για προϊόντα.",
      "Λίστα αγαπημένων (wishlist): τα προϊόντα που αποθηκεύεις ως αγαπημένα στον λογαριασμό σου.",
      "Μηνύματα υποστήριξης: το περιεχόμενο της συνομιλίας σου μέσω του chat εξυπηρέτησης.",
      "Τεχνικά δεδομένα: cookies, βασικά δεδομένα χρήσης και περιήγησης (δες την Πολιτική Cookies).",
    ],
  },
  {
    emoji: "🔎",
    title: "Από πού συλλέγουμε τα δεδομένα σου",
    paragraphs: ["Τα δεδομένα σου προέρχονται από τις εξής πηγές:"],
    bullets: [
      "Απευθείας από εσένα: όταν δημιουργείς λογαριασμό, κάνεις παραγγελία, ζητάς τιμολόγιο, γράφεις αξιολόγηση ή επικοινωνείς μαζί μας.",
      "Αυτόματα κατά την περιήγηση: μέσω cookies και παρόμοιων τεχνολογιών (δες την Πολιτική Cookies).",
      "Από τρίτους συνεργάτες: π.χ. από τις εταιρείες courier για την κατάσταση της αποστολής σου και από την ΑΑΔΕ για την επαλήθευση των στοιχείων ΑΦΜ όταν ζητάς τιμολόγιο.",
    ],
  },
  {
    emoji: "🔑",
    title: "Σύνδεση μέσω Google (Google Sign-In) & δεδομένα χρήστη Google",
    paragraphs: [
      "Το κατάστημά μας προσφέρει τη δυνατότητα σύνδεσης/εγγραφής μέσω του λογαριασμού σου Google (Google Sign-In / Google OAuth). Παρακάτω περιγράφουμε αναλυτικά πώς η εφαρμογή μας προσπελαύνει, χρησιμοποιεί, αποθηκεύει και μοιράζεται τα δεδομένα χρήστη Google (Google user data).",
    ],
    bullets: [
      "Δεδομένα που προσπελαύνουμε (Data Accessed): όταν επιλέγεις «Σύνδεση με Google», λαμβάνουμε από τη Google μόνο τα βασικά στοιχεία του προφίλ σου μέσω των πεδίων (scopes) email και profile — δηλαδή τη διεύθυνση email, το όνομα/εμφανιζόμενο όνομα, την εικόνα προφίλ και το μοναδικό αναγνωριστικό του λογαριασμού Google (Google account ID). Δεν αποκτούμε πρόσβαση στο Gmail, στις επαφές, στο ημερολόγιο ή σε άλλα δεδομένα του λογαριασμού σου Google.",
      "Πώς τα χρησιμοποιούμε (Data Usage): χρησιμοποιούμε αυτά τα δεδομένα αποκλειστικά για να δημιουργήσουμε και να ταυτοποιήσουμε τον λογαριασμό σου, να σε συνδέσουμε με ασφάλεια, να προσυμπληρώσουμε το όνομα/email σου και να επικοινωνούμε μαζί σου για την παραγγελία σου. Δεν τα χρησιμοποιούμε για διαφημιστική στόχευση.",
      "Με ποιους τα μοιραζόμαστε (Data Sharing): δεν πουλάμε και δεν διαθέτουμε τα δεδομένα χρήστη Google σε τρίτους για διαφημιστικούς σκοπούς. Τα κοινοποιούμε μόνο στους παρόχους υποδομής (φιλοξενία/βάση δεδομένων, αποστολή email) που λειτουργούν ως εκτελούντες την επεξεργασία για λογαριασμό μας, με τα κατάλληλα μέτρα προστασίας.",
      "Αποθήκευση & προστασία (Data Storage & Protection): τα στοιχεία αποθηκεύονται με ασφάλεια σε διακομιστές εντός του ΕΟΧ, με κρυπτογραφημένη μεταφορά (HTTPS), έλεγχο πρόσβασης σε επίπεδο βάσης (Row-Level Security) και περιορισμό πρόσβασης μόνο σε εξουσιοδοτημένο προσωπικό.",
      "Διατήρηση & διαγραφή (Data Retention & Deletion): διατηρούμε τα δεδομένα του λογαριασμού σου για όσο διάστημα παραμένει ενεργός ο λογαριασμός. Μπορείς ανά πάσα στιγμή να ζητήσεις τη διαγραφή τους μέσω της σελίδας «Αίτημα για τα δεδομένα μου» ή στο info@mrpanda.gr· κατόπιν αιτήματος διαγράφουμε ή ανωνυμοποιούμε τα δεδομένα σου, εκτός όσων υποχρεούμαστε να τηρούμε από τη φορολογική νομοθεσία.",
      "Περιορισμένη Χρήση (Limited Use): η χρήση και μεταφορά των πληροφοριών που λαμβάνονται από τα Google APIs σε οποιαδήποτε άλλη εφαρμογή θα τηρεί την Πολιτική Δεδομένων Χρήστη των Υπηρεσιών API της Google (Google API Services User Data Policy), συμπεριλαμβανομένων των απαιτήσεων Περιορισμένης Χρήσης (Limited Use).",
    ],
  },
  {
    emoji: "🎯",
    title: "Γιατί τα επεξεργαζόμαστε & νομική βάση",
    paragraphs: ["Επεξεργαζόμαστε τα δεδομένα σου μόνο για συγκεκριμένους, νόμιμους σκοπούς:"],
    bullets: [
      "Εκτέλεση της παραγγελίας & της σύμβασης πώλησης (άρ. 6 παρ. 1 στ. β GDPR).",
      "Συμμόρφωση με νομικές υποχρεώσεις, π.χ. έκδοση παραστατικών & φορολογικά (άρ. 6 παρ. 1 στ. γ).",
      "Έννομο συμφέρον μας, π.χ. ασφάλεια του ιστοτόπου, πρόληψη απάτης, βελτίωση υπηρεσιών (άρ. 6 παρ. 1 στ. στ).",
      "Συναίνεσή σου, π.χ. για newsletter, SMS marketing και για μη απαραίτητα cookies (άρ. 6 παρ. 1 στ. α) — μπορείς να την ανακαλέσεις ανά πάσα στιγμή.",
    ],
  },
  {
    emoji: "🤝",
    title: "Με ποιους μοιραζόμαστε τα δεδομένα",
    paragraphs: [
      "Δεν πουλάμε τα δεδομένα σου και δεν τα διαθέτουμε σε τρίτους πέραν των παρακάτω χωρίς προηγούμενη ενημέρωση ή/και τη συναίνεσή σου, εκτός αν επιβάλλεται από τον νόμο. Τα κοινοποιούμε μόνο σε συνεργάτες που μας βοηθούν να ολοκληρώσουμε την παραγγελία σου και πάντα με τα κατάλληλα μέτρα προστασίας (εκτελούντες την επεξεργασία):",
    ],
    bullets: [
      "Πάροχοι πληρωμών: Viva.com (κάρτα, IRIS κ.λπ.) και Klarna (πληρωμή σε δόσεις / αργότερα) — η Klarna μπορεί να κάνει δικό της πιστοληπτικό έλεγχο ως ανεξάρτητος υπεύθυνος επεξεργασίας.",
      "Εταιρείες μεταφορών/courier: ACS, ΕΛΤΑ Courier, BoxNow, Skroutz Last Mile, Courier Center — για την παράδοση.",
      "Δημόσια διοίκηση: ΑΑΔΕ, για επαλήθευση στοιχείων ΑΦΜ όταν ζητάς τιμολόγιο.",
      "Πάροχοι υποδομής & email: Lovable / Supabase και Cloudflare (φιλοξενία και βάση δεδομένων), Resend (αποστολή email επιβεβαιώσεων).",
      "Πάροχος τεχνητής νοημοσύνης: για τη λειτουργία του βοηθού συνομιλίας Panda (επεξεργάζεται μόνο τα μηνύματα που γράφεις στη συνομιλία).",
      "Google Maps: για εύρεση/επαλήθευση διεύθυνσης και υπολογισμό απόστασης παράδοσης.",
      "Αρμόδιες αρχές: όταν αυτό απαιτείται από τον νόμο.",
      "Εταιρικός μετασχηματισμός: σε περίπτωση συγχώνευσης, εξαγοράς ή μεταβίβασης της επιχείρησης, τα δεδομένα ενδέχεται να μεταβιβαστούν στον νέο φορέα, πάντα με τις ίδιες εγγυήσεις προστασίας.",
    ],
  },
  {
    emoji: "🌍",
    title: "Διαβιβάσεις εκτός ΕΕ",
    paragraphs: [
      "Ορισμένοι συνεργάτες (π.χ. Google, Cloudflare, Resend, πάροχος AI) ενδέχεται να επεξεργάζονται δεδομένα εκτός Ευρωπαϊκού Οικονομικού Χώρου. Σε αυτές τις περιπτώσεις, η διαβίβαση γίνεται με τις κατάλληλες εγγυήσεις που προβλέπει ο GDPR (π.χ. EU-US Data Privacy Framework ή Τυποποιημένες Συμβατικές Ρήτρες / Standard Contractual Clauses).",
    ],
  },
  {
    emoji: "🚨",
    title: "Παραβίαση δεδομένων",
    paragraphs: [
      "Εφαρμόζουμε τεχνικά και οργανωτικά μέτρα ασφαλείας (κρυπτογραφημένη σύνδεση HTTPS, περιορισμένη πρόσβαση, αντίγραφα ασφαλείας). Σε περίπτωση παραβίασης δεδομένων προσωπικού χαρακτήρα, ενημερώνουμε την Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (www.dpa.gr) εντός 72 ωρών από τη στιγμή που θα τη διαπιστώσουμε (άρθρο 33 GDPR) και, όταν υπάρχει υψηλός κίνδυνος για σένα, σε ενημερώνουμε και εσένα χωρίς αδικαιολόγητη καθυστέρηση (άρθρο 34 GDPR).",
    ],
  },
  {
    emoji: "⏳",
    title: "Πόσο καιρό τα κρατάμε",
    paragraphs: ["Διατηρούμε τα δεδομένα σου μόνο για όσο χρειάζεται:"],
    bullets: [
      "Παραστατικά & δεδομένα συναλλαγών: για όσο επιβάλλει η φορολογική νομοθεσία (κατά κανόνα έως 5+ έτη).",
      "Στοιχεία λογαριασμού: για όσο διατηρείς ενεργό λογαριασμό· διαγράφονται/ανωνυμοποιούνται μετά από αίτημά σου.",
      "Δεδομένα marketing: μέχρι να ανακαλέσεις τη συναίνεσή σου.",
      "Μηνύματα επικοινωνίας: για εύλογο διάστημα ώστε να εξυπηρετήσουμε το αίτημά σου.",
    ],
  },
  {
    emoji: "🔐",
    title: "Πώς τα προστατεύουμε",
    paragraphs: [
      "Εφαρμόζουμε κατάλληλα τεχνικά και οργανωτικά μέτρα: κρυπτογραφημένη μεταφορά δεδομένων (HTTPS), έλεγχο πρόσβασης σε επίπεδο βάσης (Row-Level Security), περιορισμό πρόσβασης μόνο σε εξουσιοδοτημένο προσωπικό και ασφαλή διαχείριση κωδικών. Παρόλα αυτά, καμία μέθοδος μετάδοσης στο διαδίκτυο δεν είναι 100% ασφαλής.",
    ],
  },
  {
    emoji: "📍",
    title: "Πού αποθηκεύονται τα δεδομένα σου",
    paragraphs: [
      "Τα δεδομένα σου αποθηκεύονται με ασφάλεια σε διακομιστές που βρίσκονται εντός του Ευρωπαϊκού Οικονομικού Χώρου (ΕΟΧ), με τα κατάλληλα τεχνικά και οργανωτικά μέτρα. Ορισμένα στοιχεία (π.χ. φορολογικά παραστατικά) ενδέχεται να τηρούνται και σε φυσικό αρχείο εντός των εγκαταστάσεών μας, με αντίστοιχα μέτρα ασφαλείας. Τυχόν διαβιβάσεις εκτός ΕΟΧ γίνονται μόνο με τις εγγυήσεις που περιγράφονται παραπάνω.",
    ],
  },
  {
    emoji: "✊",
    title: "Τα δικαιώματά σου",
    paragraphs: ["Ως υποκείμενο των δεδομένων, έχεις τα εξής δικαιώματα:"],
    bullets: [
      "Πρόσβαση στα δεδομένα σου και ενημέρωση για την επεξεργασία.",
      "Διόρθωση ανακριβών ή ελλιπών στοιχείων.",
      "Διαγραφή («δικαίωμα στη λήθη»), όπου δεν υπάρχει νομική υποχρέωση διατήρησης.",
      "Περιορισμός της επεξεργασίας.",
      "Φορητότητα: λήψη των δεδομένων σου σε δομημένη, αναγνώσιμη μορφή.",
      "Εναντίωση στην επεξεργασία που βασίζεται στο έννομο συμφέρον, συμπεριλαμβανομένης της κατάρτισης προφίλ για σκοπούς εμπορικής προώθησης.",
      "Ανάκληση της συναίνεσής σου ανά πάσα στιγμή.",
      "Υποβολή καταγγελίας στην Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (Λεωφ. Κηφισίας 1-3, 115 23 Αθήνα, www.dpa.gr).",
    ],
  },
  {
    emoji: "⚡",
    title: "Πώς ασκείς τα δικαιώματά σου",
    paragraphs: [
      "Για να ασκήσεις οποιοδήποτε από τα παραπάνω δικαιώματα, μπορείς να υποβάλεις αίτημα μέσω της ειδικής φόρμας στη σελίδα «Αίτημα για τα δεδομένα μου» ή στο info@mrpanda.gr. Η υποβολή του αιτήματος είναι δωρεάν.",
      "Ενδέχεται να σου ζητήσουμε να επιβεβαιώσεις την ταυτότητά σου πριν διεκπεραιώσουμε το αίτημα, για την προστασία σου. Απαντάμε χωρίς καθυστέρηση και το αργότερο εντός ενός (1) μηνός από την παραλαβή· σε σύνθετες περιπτώσεις, η προθεσμία μπορεί να παραταθεί κατά δύο (2) ακόμη μήνες, με σχετική ενημέρωσή σου.",
    ],
  },
  {
    emoji: "🤖",
    title: "Αυτοματοποιημένες αποφάσεις & profiling",
    paragraphs: [
      "Δεν λαμβάνουμε αποφάσεις που σε επηρεάζουν με νομικές ή αντίστοιχα σημαντικές συνέπειες αποκλειστικά με αυτοματοποιημένο τρόπο. Τυχόν προτάσεις προϊόντων ή προσωποποιημένο περιεχόμενο βασίζονται στη συναίνεσή σου ή στο έννομο συμφέρον μας για βελτίωση της εμπειρίας σου, και έχεις πάντα δικαίωμα εναντίωσης σε κατάρτιση προφίλ για σκοπούς εμπορικής προώθησης.",
    ],
  },
  {
    emoji: "🍪",
    title: "Cookies",
    paragraphs: [
      "Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες για τη λειτουργία και τη βελτίωση του καταστήματος. Τα μη απαραίτητα cookies ενεργοποιούνται μόνο με τη συναίνεσή σου. Δες αναλυτικά την Πολιτική Cookies και διαχειρίσου τις προτιμήσεις σου όποτε θέλεις.",
      "Αν λαμβάνεις ενημερωτικά email (newsletter), SMS marketing ή άλλη προωθητική επικοινωνία, μπορείς να διαγραφείς όποτε θέλεις, χωρίς καμία επιβάρυνση. Για email, χρησιμοποίησε τον σύνδεσμο «απεγγραφή» σε κάθε μήνυμα· για SMS, απάντησε με STOP ή επικοινώνησε μαζί μας στο info@mrpanda.gr."
    ],
  },
  {
    emoji: "👶",
    title: "Ανήλικοι",
    paragraphs: [
      "Το κατάστημά μας απευθύνεται σε ενήλικες. Σύμφωνα με το άρθρο 8 GDPR και το άρθρο 21 του Ν. 4624/2019, άτομα κάτω των 15 ετών μπορούν να δημιουργήσουν λογαριασμό ή να εγγραφούν στο newsletter μόνο με τη συναίνεση του γονέα ή κηδεμόνα. Με την εγγραφή δηλώνεις ότι είσαι τουλάχιστον 15 ετών ή ότι έχεις τη συναίνεση αυτή. Αν διαπιστώσουμε ότι συλλέξαμε δεδομένα παιδιού χωρίς συναίνεση, τα διαγράφουμε.",
    ],
  },
  {
    emoji: "🔄",
    title: "Αλλαγές στην πολιτική",
    paragraphs: [
      "Ενδέχεται να επικαιροποιούμε την παρούσα Πολιτική Απορρήτου. Κάθε αλλαγή αναρτάται σε αυτή τη σελίδα με ενημερωμένη ημερομηνία.",
    ],
  },
];

function PrivacyPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">🐼</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <ShieldCheck className="h-4 w-4" />
            Τα δεδομένα σου, ασφαλή μαζί μας
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Πολιτική Απορρήτου</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Εδώ θα βρεις με απλά λόγια ποια δεδομένα συλλέγουμε, γιατί, και ποια είναι τα δικαιώματά σου.
            Η ιδιωτικότητά σου είναι προτεραιότητα για εμάς. 🐼
          </p>
          <p className="mt-3 text-xs text-muted-foreground">Τελευταία ενημέρωση: {LAST_UPDATED}</p>
        </div>

        {/* Sections */}
        <div className="mt-10 space-y-4">
          {sections.map((s, i) => (
            <Card key={i} className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl transition-transform group-hover:scale-110">
                    {s.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-primary/70">{i + 1}.</span>
                      <h2 className="text-lg font-semibold tracking-tight">{s.title}</h2>
                    </div>
                    <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
                      {s.paragraphs.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      {s.bullets && (
                        <ul className="mt-2 space-y-2">
                          {s.bullets.map((b, k) => (
                            <li key={k} className="flex items-start gap-2">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-8">
          <p className="text-2xl">✊</p>
          <h2 className="mt-2 text-xl font-semibold">Θέλεις να ασκήσεις τα δικαιώματά σου;</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Στείλε μας αίτημα για πρόσβαση, διόρθωση, διαγραφή ή φορητότητα των δεδομένων σου και θα το διαχειριστούμε άμεσα.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link to="/data-request">
                Αίτημα για τα δεδομένα μου
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/cookies">Πολιτική Cookies</Link>
            </Button>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/privacy" },
      { title: "Πολιτική Απορρήτου — MrPanda.gr" },
      {
        name: "description",
        content:
          "Πώς το MrPanda.gr συλλέγει, χρησιμοποιεί και προστατεύει τα προσωπικά σου δεδομένα — σύμφωνα με τον GDPR.",
      },
      { property: "og:title", content: "Πολιτική Απορρήτου — MrPanda.gr" },
      {
        property: "og:description",
        content: "Διάβασε πώς προστατεύουμε τα προσωπικά σου δεδομένα στο MrPanda.gr.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/privacy" }],
  }),
  component: PrivacyPage,
});
